import { useState, useEffect, useRef } from 'react';
import { Flag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Shared "flag this question" affordance for vocab surfaces.
 *
 * Behaviour:
 *   - Renders a small flag icon button when `enabled` is true.
 *   - On click, opens a small modal with reason picker + freeform text.
 *   - Submits to `endpoint` with a JSON body of identifiers + reason.
 *   - On success, swaps the button for a transient "Flagged — thanks"
 *     pill that auto-dismisses after 3s and then restores the icon.
 *
 * Visibility (today): callers only render this when an impersonator or
 * teacher is on the page — real students don't see it yet. The component
 * doesn't enforce that itself; the parent gates with `enabled`.
 *
 * Props:
 *   enabled         — boolean. When false, renders nothing.
 *   endpoint        — POST URL (e.g. "/student/vocabulary/flags" or the
 *                     teacher-scoped equivalent).
 *   payload         — object merged into the request body, identifies what
 *                     is being flagged: { user_word_card_id, vocabulary_session_id,
 *                     word_review_log_id, vocabulary_list_word_id, stage_or_format }.
 *   className       — optional positioning class. Default places the button
 *                     inline; callers position via wrapper or override.
 */
const REASONS = [
  { value: 'context_missing', label: "Context doesn't contain the word" },
  { value: 'definition_circular', label: 'Definition is circular or unclear' },
  { value: 'options_ambiguous', label: 'Options are ambiguous' },
  { value: 'word_doesnt_fit', label: "Word doesn't fit the slot" },
  { value: 'spelling_issue', label: 'Spelling or capitalisation issue' },
  { value: 'other', label: 'Something else' },
];

export default function FlagQuestionButton({
  enabled,
  endpoint,
  payload,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('context_missing');
  const [reasonText, setReasonText] = useState('');
  const [error, setError] = useState(null);
  const closeTimerRef = useRef(null);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  if (!enabled) return null;

  const reset = () => {
    setOpen(false);
    setReason('context_missing');
    setReasonText('');
    setError(null);
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (reason === 'other' && !reasonText.trim()) {
      setError('Please describe the issue.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({
          ...(payload || {}),
          reason,
          reason_text: reasonText.trim() || null,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.errors?.join(', ') || 'Could not submit flag.');
      }

      setConfirmed(true);
      reset();
      closeTimerRef.current = setTimeout(() => setConfirmed(false), 3000);
    } catch (err) {
      setError(err.message || 'Could not submit flag.');
      setSubmitting(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {!open && !confirmed && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-rose-500 dark:text-gray-500 dark:hover:text-rose-400 transition-colors"
          aria-label="Flag this question"
          title="Flag this question"
        >
          <Flag className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Flag</span>
        </button>
      )}

      {confirmed && (
        <motion.span
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"
          role="status"
        >
          <Flag className="w-3.5 h-3.5" />
          Flagged — thanks
        </motion.span>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) reset();
            }}
          >
            <motion.div
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 8, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
            >
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-rose-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Flag this question
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3">
                <fieldset className="space-y-2">
                  <legend className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                    Reason
                  </legend>
                  {REASONS.map((r) => (
                    <label
                      key={r.value}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="vocab-flag-reason"
                        value={r.value}
                        checked={reason === r.value}
                        onChange={() => setReason(r.value)}
                        className="text-rose-500 focus:ring-rose-500"
                      />
                      {r.label}
                    </label>
                  ))}
                </fieldset>

                <div>
                  <label
                    htmlFor="vocab-flag-reason-text"
                    className="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium mb-1"
                  >
                    Notes {reason === 'other' && <span className="text-rose-500">*</span>}
                  </label>
                  <textarea
                    id="vocab-flag-reason-text"
                    rows={3}
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    placeholder={reason === 'other'
                      ? 'Describe what looks off...'
                      : 'Anything you want to add (optional)'}
                    className="w-full text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-gray-900 dark:text-gray-100 focus:border-rose-400 focus:ring-1 focus:ring-rose-400 focus:outline-none"
                  />
                </div>

                {error && (
                  <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
                )}

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={reset}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 disabled:opacity-60 rounded-md"
                  >
                    {submitting ? 'Sending...' : 'Submit flag'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
